import type { EnemyTemplate, EnemyFormTransition } from '../../../types';
import { getPortraitUrl, getFullSpriteUrl } from '../../../combat/spriteConfig';
import { formatDefensiveTypesColored, elementClass } from '../../components/GameTerms';
import { getConfig, getDiscoveredEnemies } from './types';

export interface BestiaryState {
  expandedEnemyId: string | null;
  elementFilter: string;
}

export function createBestiaryState(): BestiaryState {
  return { expandedEnemyId: null, elementFilter: '' };
}

/** Templates referenced as a formTransitions target by another template. These
 *  are mid-fight engine swaps (e.g. Velara's form 2 / form 3) and should NOT
 *  appear as standalone bestiary cards — they belong inside their parent's
 *  detail page as Phases. */
function getFormChildIds(enemies: Record<string, EnemyTemplate>): Set<string> {
  const childIds = new Set<string>();
  for (const e of Object.values(enemies)) {
    for (const t of e.formTransitions ?? []) {
      childIds.add(t.templateId);
    }
  }
  return childIds;
}

/** Walk the form chain starting from the given parent. Returns one entry per
 *  phase including the parent (phase 1) plus each transition target in HP-order.
 *  Each entry's threshold band is derived from the chain's atHpPercent values. */
interface PhaseEntry {
  template: EnemyTemplate;
  /** "Above 67% HP", "Below 67% HP", "Below 33% HP" — phase-band label */
  bandLabel: string;
}

function walkFormChain(parent: EnemyTemplate, enemies: Record<string, EnemyTemplate>): PhaseEntry[] {
  const phases: PhaseEntry[] = [];
  let current: EnemyTemplate | undefined = parent;
  let upperPercent = 100;

  while (current) {
    const next: EnemyFormTransition | undefined = current.formTransitions?.[0];
    const lowerPercent = next?.atHpPercent;
    let bandLabel: string;
    if (phases.length === 0 && lowerPercent != null) {
      bandLabel = `Above ${lowerPercent}% HP`;
    } else if (lowerPercent != null) {
      bandLabel = `${lowerPercent}–${upperPercent}% HP`;
    } else {
      bandLabel = phases.length === 0 ? 'Single phase' : `Below ${upperPercent}% HP`;
    }
    phases.push({ template: current, bandLabel });

    if (!next) break;
    upperPercent = next.atHpPercent;
    current = enemies[next.templateId];
  }

  return phases;
}

export function renderBestiary(content: HTMLElement, state: BestiaryState): void {
  if (state.expandedEnemyId) {
    renderEnemyDetailPage(content, state);
    return;
  }

  const config = getConfig();
  if (!config?.enemies) { content.innerHTML = '<p>Loading...</p>'; return; }

  const formChildIds = getFormChildIds(config.enemies);
  const allEnemies = Object.values(config.enemies).filter(e => !formChildIds.has(e.id));
  const discovered = getDiscoveredEnemies();
  const discoveredCount = allEnemies.filter(e => discovered.has(e.id)).length;
  const dtSet = new Set<string>();
  for (const e of allEnemies) {
    for (const dt of (e.defensiveTypes ?? [])) dtSet.add(dt);
  }
  const elements = [...dtSet].sort();

  const enemies = state.elementFilter
    ? allEnemies.filter(e => (e.defensiveTypes ?? []).includes(state.elementFilter))
    : allEnemies;

  content.innerHTML = `
    <h2 class="codex-section-title">Bestiary</h2>
    <p class="codex-section-subtitle">${discoveredCount} / ${allEnemies.length} discovered</p>
    <div class="codex-filter-pills">
      <button class="codex-pill${!state.elementFilter ? ' active' : ''}" data-el="">All</button>
      ${elements.map(el => `<button class="codex-pill ${elementClass(el)}${state.elementFilter === el ? ' active' : ''}" data-el="${el}">${el}</button>`).join('')}
    </div>
    <div class="codex-card-grid codex-card-grid--tall">${enemies.map(e => renderEnemyCard(e, discovered.has(e.id))).join('')}</div>
  `;

  content.querySelectorAll('.codex-pill').forEach(pill => {
    pill.addEventListener('click', () => {
      state.elementFilter = (pill as HTMLElement).dataset.el || '';
      renderBestiary(content, state);
    });
  });

  content.querySelectorAll('.codex-card[data-enemy-id]:not(.undiscovered)').forEach(card => {
    card.addEventListener('click', () => {
      state.expandedEnemyId = (card as HTMLElement).dataset.enemyId!;
      renderBestiary(content, state);
      content.scrollTop = 0;
    });
  });
}

function renderEnemyCard(e: EnemyTemplate, isDiscovered: boolean): string {
  const src = getPortraitUrl(e.spriteKey);
  if (!isDiscovered) {
    return `<div class="codex-card codex-card--tall undiscovered" data-enemy-id="${e.id}">
      <div class="codex-card-sprite">${src ? `<img class="codex-sprite-img codex-sprite-silhouette" src="${src}" alt="Unknown" loading="lazy" draggable="false" />` : ''}</div>
      <div class="codex-card-name">???</div>
    </div>`;
  }
  const types = e.defensiveTypes ?? [];
  const dtLabel = types.length > 0
    ? formatDefensiveTypesColored(types)
    : `<span class="${elementClass(e.element)}">${e.element}</span>`;
  return `<div class="codex-card codex-card--tall" data-enemy-id="${e.id}">
    <div class="codex-card-sprite"><img class="codex-sprite-img" src="${src}" alt="${e.name}" loading="lazy" draggable="false" /></div>
    <div class="codex-card-name">${e.name}</div>
    <div class="codex-card-meta">${dtLabel}</div>
  </div>`;
}

function renderAbilityRows(abilityIds: string[], abilities: Record<string, { name: string; element: string; type: string }>): string {
  return abilityIds.map(id => {
    const ab = abilities[id];
    if (!ab) return '';
    return `<div class="codex-stat-row"><span class="codex-stat-label">${ab.name}</span><span class="codex-stat-value"><span class="${elementClass(ab.element)}">${ab.element}</span> · ${ab.type}</span></div>`;
  }).join('');
}

function renderEnemyDetailPage(content: HTMLElement, state: BestiaryState): void {
  const config = getConfig();
  if (!config?.enemies || !state.expandedEnemyId) { state.expandedEnemyId = null; renderBestiary(content, state); return; }

  const e = config.enemies[state.expandedEnemyId];
  if (!e) { state.expandedEnemyId = null; renderBestiary(content, state); return; }

  const fullSrc = getFullSpriteUrl(e.spriteKey);
  const portraitSrc = getPortraitUrl(e.spriteKey);
  const spriteSrc = fullSrc || portraitSrc;

  const hasPhases = (e.formTransitions ?? []).length > 0;
  const phases = hasPhases ? walkFormChain(e, config.enemies) : [];

  // For multi-form bosses, the per-phase ability lists carry the kit info;
  // the top-level Abilities card would just duplicate phase 1, so suppress it.
  const abilitiesHtml = !hasPhases
    ? renderAbilityRows(e.abilityIds, config.abilities)
    : '';

  const phasesHtml = hasPhases
    ? phases.map((p, i) => {
        const phaseSpriteSrc = getPortraitUrl(p.template.spriteKey);
        const rows = renderAbilityRows(p.template.abilityIds, config.abilities);
        return `<div class="codex-phase-row">
          <div class="codex-phase-header">
            ${phaseSpriteSrc ? `<img class="codex-phase-sprite" src="${phaseSpriteSrc}" alt="Phase ${i + 1}" loading="lazy" draggable="false" />` : ''}
            <div class="codex-phase-meta">
              <div class="codex-phase-title">Phase ${i + 1}</div>
              <div class="codex-phase-band">${p.bandLabel}</div>
            </div>
          </div>
          <div class="codex-phase-abilities">${rows}</div>
        </div>`;
      }).join('')
    : '';

  content.innerHTML = `
    <button class="codex-back-btn" id="codex-enemy-back">Back</button>
    <div class="codex-detail-page">
      <div class="codex-detail-hero">
        <div class="codex-detail-sprite"><img src="${spriteSrc}" alt="${e.name}" draggable="false" /></div>
        <div class="codex-detail-hero-info">
          <h2 class="codex-detail-name">${e.name}</h2>
          <div class="codex-detail-tags">
            ${(e.defensiveTypes && e.defensiveTypes.length > 0
              ? e.defensiveTypes
              : [e.element]
            ).map(dt => `<span class="codex-tag element ${elementClass(dt)}">${dt}</span>`).join('')}
          </div>
          <div class="codex-detail-stats">
            <div class="codex-stat-block"><span class="codex-stat-num">${e.hp}</span><span class="codex-stat-lbl">Health</span></div>
            <div class="codex-stat-block"><span class="codex-stat-num">${e.spd}</span><span class="codex-stat-lbl">Speed</span></div>
          </div>
        </div>
      </div>
      ${abilitiesHtml ? `<div class="codex-detail-card"><div class="codex-detail-card-title">Abilities</div>${abilitiesHtml}</div>` : ''}
      ${phasesHtml ? `<div class="codex-detail-card"><div class="codex-detail-card-title">Phases</div>${phasesHtml}</div>` : ''}
    </div>
  `;

  content.querySelector('#codex-enemy-back')?.addEventListener('click', () => {
    state.expandedEnemyId = null;
    renderBestiary(content, state);
    content.scrollTop = 0;
  });
}
